/**
 * AlarmCatalogPanel — chuleta de consulta de códigos de alarma. Ayuda a
 * interpretar un código que aparece en el panel de alarmas o en el
 * histórico sin tener que ir a buscar la spec del Protocolo B.
 *
 * Incluye alta/baja de códigos propios del municipio (SC-43, admin) —
 * guardado directo, mismo patrón que el resto de Modo Edición.
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Search, Plus, Trash2 } from 'lucide-react';
import { useAlarmCatalogPanel } from '../store/alarmCatalogPanel';
import { ALARM_CATALOG } from '../data/alarmCatalog';
import { customAlarmCodes } from '../data/overridesRuntime';
import { useOverridesVersion } from '../store/overridesVersion';
import { useCustomAlarmCodeDraft } from '../store/customAlarmCodeDraft';
import { useAuth } from '../hooks/useAuth';

export function AlarmCatalogPanel() {
  const open = useAlarmCatalogPanel(s => s.open);
  const close = useAlarmCatalogPanel(s => s.close);
  const isAdmin = useAuth(s => s.user?.is_admin === true);
  useOverridesVersion(s => s.version); // re-render al dar de alta/baja un código
  const stage = useCustomAlarmCodeDraft(s => s.stage);
  const remove = useCustomAlarmCodeDraft(s => s.remove);

  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [descInput, setDescInput] = useState('');
  const [formError, setFormError] = useState('');

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const filtered = ALARM_CATALOG.filter(e =>
    !q || e.code.toLowerCase().includes(q) || e.description.toLowerCase().includes(q),
  );
  const filteredCustom = customAlarmCodes.filter(e =>
    !q || e.code.toLowerCase().includes(q) || e.description.toLowerCase().includes(q),
  );

  const submitAdd = async () => {
    const code = codeInput.trim();
    const description = descInput.trim();
    if (!code || !description) {
      setFormError('Código y descripción son obligatorios');
      return;
    }
    if (customAlarmCodes.some(c => c.code === code)) {
      setFormError(`Ya existe el código "${code}"`);
      return;
    }
    if (!(await stage({ code, description }))) {
      setFormError('No se pudo guardar — reintenta');
      return;
    }
    setCodeInput('');
    setDescInput('');
    setFormError('');
    setAdding(false);
  };

  return (
    <motion.div
      initial={{ x: -340, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="absolute top-0 left-0 bottom-0 w-[360px] bg-gray-950/95 backdrop-blur-md
                 border-r border-gray-700/40 z-40 flex flex-col shadow-2xl"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <div>
          <div className="text-sm font-semibold text-white">Chuleta de alarmas</div>
          <div className="text-[10px] text-gray-500">Códigos del Protocolo B y notas de campo</div>
        </div>
        <button
          onClick={close}
          className="p-1.5 rounded hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
          title="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 py-2.5 border-b border-gray-800">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar por código o texto…"
            className="w-full bg-gray-800 text-gray-100 text-[12px] pl-8 pr-2.5 py-1.5 rounded border border-gray-700 focus:border-cyan-500 focus:outline-none"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {(filteredCustom.length > 0 || isAdmin) && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-2">
              Personalizadas de este municipio
            </div>
            <div className="space-y-1.5">
              {filteredCustom.map(e => (
                <AlarmRow key={e.code} entry={e} onRemove={isAdmin ? () => remove(e.code) : undefined} />
              ))}
            </div>

            {isAdmin && (
              adding ? (
                <div className="mt-2 space-y-1.5 rounded border border-gray-700 bg-gray-900/60 p-2">
                  <input
                    type="text"
                    value={codeInput}
                    onChange={e => setCodeInput(e.target.value)}
                    placeholder="Código (ej. VR)"
                    className="w-full bg-gray-800 text-gray-100 font-mono text-[11px] px-2 py-1 rounded border border-gray-700 focus:border-cyan-500 focus:outline-none"
                  />
                  <input
                    type="text"
                    value={descInput}
                    onChange={e => setDescInput(e.target.value)}
                    placeholder="Descripción (ej. Ventilador roto)"
                    className="w-full bg-gray-800 text-gray-100 text-[11px] px-2 py-1 rounded border border-gray-700 focus:border-cyan-500 focus:outline-none"
                  />
                  {formError && <div className="text-[10px] text-red-400">{formError}</div>}
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => { setAdding(false); setFormError(''); }}
                      className="flex-1 px-2 py-1 rounded text-[10px] font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={submitAdd}
                      className="flex-1 px-2 py-1 rounded text-[10px] font-medium bg-cyan-700 text-white hover:bg-cyan-600 transition-colors"
                    >
                      Guardar
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setAdding(true)}
                  className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-medium
                             bg-gray-800 text-gray-300 border border-gray-700 hover:bg-gray-700 transition-colors"
                >
                  <Plus className="w-3 h-3" />Añadir código
                </button>
              )
            )}
          </div>
        )}

        <div className="space-y-1.5">
          {filtered.map(e => <AlarmRow key={e.code} entry={e} />)}
        </div>
      </div>
    </motion.div>
  );
}

function AlarmRow({ entry, onRemove }: { entry: { code: string; description: string }; onRemove?: () => void }) {
  return (
    <div className="flex items-start gap-2.5 rounded border border-gray-800 bg-gray-900/40 px-2.5 py-1.5">
      <span className="flex-shrink-0 text-[11px] font-mono font-semibold text-cyan-300 bg-cyan-900/20 border border-cyan-700/40 rounded px-1.5 py-0.5 min-w-[2.5rem] text-center">
        {entry.code}
      </span>
      <div className="text-[11px] text-gray-200 leading-snug min-w-0 flex-1">{entry.description}</div>
      {onRemove && (
        <button
          onClick={onRemove}
          title="Quitar código"
          className="flex-shrink-0 text-gray-600 hover:text-red-400 transition-colors"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
